import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { useTranslation } from 'react-i18next';
import { useTheme } from './AdminLayout';

export default function QRScannerModal({ isOpen, onClose, onScanSuccess }) {
    const { t } = useTranslation();
    const { T } = useTheme();
    const scannerRef = useRef(null);
    const dialogRef = useRef(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (isOpen && dialogRef.current) {
            dialogRef.current.focus();
        }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) {
            if (scannerRef.current) {
                scannerRef.current.stop().then(() => {
                    scannerRef.current.clear();
                    scannerRef.current = null;
                }).catch(console.error);
            }
            return;
        }

        const startScanner = async () => {
            try {
                const html5QrCode = new Html5Qrcode("reader");
                scannerRef.current = html5QrCode;

                await html5QrCode.start(
                    { facingMode: "environment" },
                    {
                        fps: 10,
                        qrbox: { width: 250, height: 250 }
                    },
                    (decodedText) => {
                        if (scannerRef.current) {
                            scannerRef.current.stop().then(() => {
                                scannerRef.current.clear();
                                scannerRef.current = null;
                                onScanSuccess(decodedText);
                            }).catch((err) => {
                                console.error("Failed to stop scanner", err);
                            });
                        }
                    },
                    (errorMessage) => {
                        // We can ignore continuous scanning warnings.
                    }
                );
            } catch (err) {
                setError(t('admin.cameraError') || "Failed to access camera.");
                console.error(err);
            }
        };

        startScanner();

        return () => {
            if (scannerRef.current) {
                scannerRef.current.stop().then(() => {
                    scannerRef.current.clear();
                    scannerRef.current = null;
                }).catch(console.error);
            }
        };
    }, [isOpen, onScanSuccess, t]);

    if (!isOpen) return null;

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="qr-scanner-title"
            tabIndex={-1}
            onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
            style={{
                position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.85)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                zIndex: 9999, padding: '1rem', outline: 'none'
            }}
        >
            <div style={{
                background: T.card, padding: '2rem', borderRadius: 16, border: `1px solid ${T.border}`,
                width: '100%', maxWidth: 500, display: 'flex', flexDirection: 'column', alignItems: 'center'
            }}>
                <h2 id="qr-scanner-title" style={{ fontFamily: "'Oswald', sans-serif", fontSize: '1.25rem', color: T.white, marginBottom: '1rem' }}>
                    {t('admin.scanQR') || "Scan Reservation QR Code"}
                </h2>

                {error ? (
                    <p style={{ color: 'red', marginBottom: '1rem' }}>{error}</p>
                ) : (
                    <div id="reader" style={{ width: '100%', marginBottom: '1rem', overflow: 'hidden', borderRadius: 12 }}></div>
                )}

                <button
                    onClick={onClose}
                    style={{
                        padding: '0.75rem 1.5rem', background: '#333', color: T.white,
                        border: 'none', borderRadius: 8, cursor: 'pointer', fontFamily: "'Oswald', sans-serif"
                    }}
                >
                    {t('common.cancel') || "Cancel"}
                </button>
            </div>
        </div>
    );
}
